import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export type SummaryEventHandler = (
  event: "domain_opened" | "domain_grade_opened" | "hover_opened",
  properties: Record<string, string | number>,
) => void;

type Props = {
  children: ReactNode;
  title: string;
  lines: ReactNode[];
  className?: string;
  label?: string;
  style?: CSSProperties;
  onOpen?: () => void;
};

export default function SummaryTooltip({
  children,
  title,
  lines,
  className = "",
  label,
  style,
  onOpen,
}: Props) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const opened = useRef(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const isOpen = position !== null;
  const maxHeight = position?.maxHeight;

  const close = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
    opened.current = false;
    setPosition(null);
  }, []);
  const retain = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const leave = () => {
    retain();
    closeTimer.current = setTimeout(close, 150);
  };
  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );
  const show = () => {
    retain();
    if (!trigger.current || opened.current) return;
    const rect = trigger.current.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportWidth = viewport?.width ?? window.innerWidth;
    const viewportLeft = viewport?.offsetLeft ?? 0;
    const viewportTop = viewport?.offsetTop ?? 0;
    const viewportHeight = viewport?.height ?? window.innerHeight;
    const width = Math.min(320, viewportWidth - 16);
    const left = Math.max(
      viewportLeft + 8,
      Math.min(
        rect.left + rect.width / 2 - width / 2,
        viewportLeft + viewportWidth - width - 8,
      ),
    );
    setPosition({
      top: viewportTop + 8,
      left,
      width,
      maxHeight: Math.max(1, viewportHeight - 16),
    });
    onOpen?.();
    opened.current = true;
  };

  useLayoutEffect(() => {
    if (!isOpen || maxHeight === undefined || !trigger.current || !card.current)
      return;
    const place = () => {
      if (!trigger.current || !card.current) return;
      const rect = trigger.current.getBoundingClientRect();
      const height = card.current.getBoundingClientRect().height;
      const minimum = (window.visualViewport?.offsetTop ?? 0) + 8;
      const maximum = minimum + maxHeight;
      const above = rect.top - 8 - minimum;
      const below = maximum - rect.bottom - 8;
      const preferred =
        height <= above || (height > below && above >= below)
          ? rect.top - 8 - height
          : rect.bottom + 8;
      const top = Math.max(minimum, Math.min(preferred, maximum - height));
      setPosition((previous) =>
        previous && previous.top !== top ? { ...previous, top } : previous,
      );
    };
    place();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(place);
    observer.observe(card.current);
    return () => observer.disconnect();
  }, [isOpen, maxHeight, title, lines]);

  useEffect(() => {
    if (!isOpen) return;
    const dismiss = (event: PointerEvent) => {
      const node = event.target as Node;
      if (!trigger.current?.contains(node) && !card.current?.contains(node))
        close();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const scroll = (event: Event) => {
      if (event.target instanceof Node && card.current?.contains(event.target))
        return;
      close();
    };
    const viewport = window.visualViewport;
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", close);
    viewport?.addEventListener("scroll", close);
    viewport?.addEventListener("resize", close);
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      window.removeEventListener("scroll", scroll, true);
      window.removeEventListener("resize", close);
      viewport?.removeEventListener("scroll", close);
      viewport?.removeEventListener("resize", close);
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [isOpen, close]);

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={`et-summary-tooltip-trigger ${className}`}
        style={style}
        aria-label={label}
        aria-describedby={position ? id : undefined}
        onMouseEnter={show}
        onMouseLeave={leave}
        onFocus={show}
        onBlur={(event) => {
          if (!card.current?.contains(event.relatedTarget)) close();
        }}
        onClick={(event) => {
          event.stopPropagation();
          show();
        }}
      >
        {children}
      </button>
      {position &&
        createPortal(
          <div
            ref={card}
            id={id}
            role="tooltip"
            tabIndex={0}
            onClick={(event) => event.stopPropagation()}
            onMouseEnter={retain}
            onMouseLeave={leave}
            onFocus={retain}
            onBlur={(event) => {
              if (
                !trigger.current?.contains(event.relatedTarget) &&
                !card.current?.contains(event.relatedTarget)
              )
                close();
            }}
            className="et-analytics-tooltip et-summary-tooltip"
            style={{
              top: position.top,
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
            }}
          >
            <strong>{title}</strong>
            {lines.slice(0, 3).map((line, index) => (
              <p key={index}>{line}</p>
            ))}
            <button
              type="button"
              className="et-summary-tooltip-close"
              aria-label="Close explanation"
              onClick={close}
            >
              ×
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}
